import clsx from 'clsx';

import { getSDDOTReplenishmentSubtextOptions } from '@atom/components/common/requests/customTenantUtilities';
import { Button, DatePicker, Icon, TextField } from '@atom/mui';
import { AttachMoneyIcon, WarningIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { ReplenishmentCreateInput } from '@atom/types/assetRequest';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import classes from './sddotView.module.css';

const DECIMAL_LIMIT = 4;

const styles = {
  label: {
    color: colors.neutral.dim,
  },
  currencyIcon: {
    marginRight: '2px',
    marginBottom: '2px',
    color: colors.neutral.dim,
    fontSize: '16px',
  },
  icon: {
    marginRight: '1rem',
  },
  largeInput: {
    width: '10rem',
  },
  smallInput: {
    width: '6rem',
  },
};

interface Props {
  assetUnit: string;
  totalOrdered: number;
  totalReceived?: number;
  replenishmentInputs: Partial<ReplenishmentCreateInput>;
  updateReplenishmentInput: (
    value: any,
    property: keyof ReplenishmentCreateInput,
  ) => void;
  onCreate?: () => void;
  onCancel?: () => void;
}

const AddReplenishmentForm = ({
  assetUnit,
  totalOrdered,
  totalReceived,
  replenishmentInputs,
  updateReplenishmentInput,
  onCreate,
  onCancel,
}: Props) => {
  const numberUpdate = (
    value: any,
    property: keyof ReplenishmentCreateInput,
  ) => {
    const newValue =
      Math.abs(Number(value)) > 0 ? Math.abs(Number(value)) : null;
    updateReplenishmentInput(newValue, property);
  };

  const handleDateChange = (
    newValue: Date | any,
    property: keyof ReplenishmentCreateInput,
  ) => {
    if (!newValue || newValue.toString() === 'Invalid Date') {
      updateReplenishmentInput(null, property);
    } else {
      const mappedDate = convertDateToMillisGMTMidday(newValue);
      updateReplenishmentInput(mappedDate, property);
    }
  };

  const costUpdate = (value: any, property: keyof ReplenishmentCreateInput) => {
    const newValue =
      value !== '' ? parseFloat(Math.abs(Number(value)).toFixed(4)) : null;
    updateReplenishmentInput(newValue, property);
  };

  const calculateValue = (property: keyof ReplenishmentCreateInput) => {
    const { quantityReceived, unitCost, totalCost } = replenishmentInputs;

    const totalCostCalc = parseFloat(
      (Number(quantityReceived) * Number(unitCost)).toFixed(DECIMAL_LIMIT),
    );
    const costCalc = parseFloat(
      (Number(totalCost) / Number(quantityReceived)).toFixed(DECIMAL_LIMIT),
    );

    const updatedValue = property === 'totalCost' ? totalCostCalc : costCalc;
    updateReplenishmentInput(updatedValue, property);
  };

  const subtextValue = getSDDOTReplenishmentSubtextOptions(
    totalOrdered,
    totalReceived,
    replenishmentInputs?.quantityReceived,
    assetUnit || '',
  );

  const subtextStyleName = clsx(
    classes.remainingSubtext,
    subtextValue?.icon === WarningIcon && classes.warning,
  );

  const iconColor =
    subtextValue?.icon === WarningIcon
      ? colors.brand.orange
      : colors.brand.green;

  const showUnitCostCalculate =
    isNilOrEmpty(replenishmentInputs?.unitCost) &&
    !isNilOrEmpty(replenishmentInputs?.quantityReceived) &&
    !isNilOrEmpty(replenishmentInputs?.totalCost);

  const showTotalCostCalculate =
    isNilOrEmpty(replenishmentInputs?.totalCost) &&
    !isNilOrEmpty(replenishmentInputs?.quantityReceived) &&
    !isNilOrEmpty(replenishmentInputs?.unitCost);

  const createDisabled =
    isNilOrEmpty(replenishmentInputs?.invoiceNumber) ||
    isNilOrEmpty(replenishmentInputs?.dateReceived) ||
    isNilOrEmpty(replenishmentInputs?.quantityReceived) ||
    isNilOrEmpty(replenishmentInputs?.unitCost) ||
    isNilOrEmpty(replenishmentInputs?.totalCost);

  const dateReceived = replenishmentInputs?.dateReceived
    ? new Date(replenishmentInputs?.dateReceived)
    : null;

  return (
    <div className={classes.formContainer}>
      <div className={classes.formTitle}>Replenishment</div>
      <div className={classes.formSubtext}>
        The unit cost of this inventory item will be re-calculated by taking
        into account the quantity received and the unit cost of this
        replenishment.
      </div>
      <div className={classes.formInputRow}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          style={styles.largeInput}
          value={replenishmentInputs?.invoiceNumber}
          label="Invoice Number"
          onChange={event =>
            updateReplenishmentInput(event.target.value, 'invoiceNumber')
          }
          data-cy="assetRequestReplenishmentFormInvoiceNumber"
        />
        <DatePicker
          inputLabelSx={styles.label}
          style={styles.largeInput}
          label="Date Received"
          value={dateReceived}
          onChange={dateValue => handleDateChange(dateValue, 'dateReceived')}
          data-cy="assetRequestReplenishmentFormDateReceived"
        />
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          style={styles.largeInput}
          type="number"
          value={replenishmentInputs?.quantityReceived || ''}
          label={`Total Quantity Received (${assetUnit || ''})`}
          onChange={event =>
            numberUpdate(event.target.value, 'quantityReceived')
          }
          data-cy="assetRequestReplenishmentFormQuantityReceived"
        />
        <TextField
          slotProps={{
            inputLabel: { style: styles.label },
            input: {
              startAdornment: (
                <Icon style={styles.currencyIcon} icon={AttachMoneyIcon} />
              ),
            },
          }}
          style={styles.smallInput}
          type="number"
          value={replenishmentInputs?.unitCost}
          label="Unit Cost"
          onChange={event => costUpdate(event.target.value, 'unitCost')}
          data-cy="assetRequestReplenishmentFormUnitCost"
          helperText={
            showUnitCostCalculate && (
              <div
                className={classes.calculateButton}
                onClick={() => calculateValue('unitCost')}
                data-cy="assetRequestReplenishmentFormUnitCostCalculate"
              >
                Calculate
              </div>
            )
          }
        />
        <TextField
          slotProps={{
            inputLabel: { style: styles.label },
            input: {
              startAdornment: (
                <Icon style={styles.currencyIcon} icon={AttachMoneyIcon} />
              ),
            },
          }}
          style={styles.smallInput}
          type="number"
          value={replenishmentInputs?.totalCost}
          label="Total Cost"
          onChange={event => costUpdate(event.target.value, 'totalCost')}
          data-cy="assetRequestReplenishmentFormTotalCost"
          helperText={
            showTotalCostCalculate && (
              <div
                className={classes.calculateButton}
                onClick={() => calculateValue('totalCost')}
                data-cy="assetRequestReplenishmentFormTotalCostCalculate"
              >
                Calculate
              </div>
            )
          }
        />
      </div>
      <div className={classes.inputContainer}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          fullWidth
          value={replenishmentInputs?.comments}
          label="Comments (optional)"
          onChange={event =>
            updateReplenishmentInput(event.target.value, 'comments')
          }
          data-cy="assetRequestReplenishmentFormComment"
        />
      </div>
      {subtextValue && (
        <div className={subtextStyleName}>
          <Icon
            color={iconColor}
            style={styles.icon}
            icon={subtextValue.icon}
          />
          <div>{subtextValue.message}</div>
        </div>
      )}
      {onCreate && onCancel && (
        <div className={classes.createButtons}>
          <Button
            onClick={onCancel}
            style={{ marginRight: '0.5rem' }}
            data-cy="assetRequestReplenishmentCancel"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={onCreate}
            disabled={createDisabled}
            data-cy="assetRequestReplenishmentCreate"
          >
            Create
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddReplenishmentForm;
